import { Link } from 'react-router-dom';
import { ArrowRight, History, Coffee, Dog, Wifi, Armchair, Phone } from 'lucide-react';
import { ASSETS } from '../components/Layout';

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-cream" data-testid="about-page">
      {/* Hero Section */}
      <section className="relative h-[60vh] min-h-[400px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={ASSETS.details}
            alt="Bean in Stony interior details"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-cream" />
        </div>
        
        <div className="relative z-10 text-center px-6">
          <p className="font-accent text-white text-2xl mb-4 drop-shadow-lg">Our Heritage</p>
          <h1 
            className="font-serif text-5xl md:text-7xl text-white font-bold drop-shadow-[0_4px_8px_rgba(0,0,0,0.5)]"
            data-testid="about-headline"
          >
            A Journey Through Time
          </h1>
        </div>
      </section>

      {/* Main Story Section */}
      <section className="section-padding" data-testid="heritage-story-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            {/* Story Content */}
            <div className="lg:col-span-7">
              <div className="prose prose-lg max-w-none">
                <p className="text-sage uppercase tracking-widest text-sm font-medium mb-6">
                  Established in the 1700s
                </p>
                
                <h2 className="font-serif text-4xl md:text-5xl text-walnut mb-8 leading-tight">
                  From Apothecary to Artisan Café
                </h2>

                <div className="text-walnut/80 space-y-6 text-lg leading-relaxed">
                  <p className="first-letter:text-6xl first-letter:font-serif first-letter:text-walnut first-letter:float-left first-letter:mr-4 first-letter:mt-1 first-letter:leading-none">
                    Step through our doors and you step back in time. Bean in Stony occupies 
                    a remarkable building that has stood at the heart of Stony Stratford since 
                    the 1700s, originally serving the community as an apothecary and chemist.
                  </p>

                  <p>
                    The building's soul remains intact — the original wooden apothecary drawers 
                    that once held mysterious remedies and healing herbs now stand as beautiful 
                    sentinels of our heritage. Their brass handles, worn smooth by centuries of 
                    hands, still catch the light just as they did when Georgian chemists reached 
                    for their preparations.
                  </p>

                  <p>
                    Above, our magnificent Tiffany-style pendant lamps cast the same warm glow 
                    that welcomed weary travellers when Stony Stratford was a crucial stop on 
                    the coaching route from London to the North. The exposed brick walls, 
                    carefully preserved, whisper stories of a bygone era.
                  </p>

                  <blockquote className="border-l-4 border-sage bg-sage/10 p-6 italic font-serif text-xl text-walnut not-italic my-8">
                    "Every corner of our café whispers stories of a bygone era, 
                    inviting you to be part of a narrative centuries in the making."
                  </blockquote>

                  <p>
                    Today, where potions were once prepared, we craft artisan coffees. 
                    Where remedies were dispensed, we now serve homemade cakes, fresh-baked 
                    scones with clotted cream, and our renowned afternoon teas. The spirit 
                    of care and craftsmanship remains the same — only the offerings have changed.
                  </p>

                  <p>
                    We invite you to pull up a chair at one of our vintage wooden tables, 
                    let the atmosphere envelop you, and become the newest chapter in our 
                    continuing story.
                  </p>
                </div>
              </div>
            </div>

            {/* Image Column */}
            <div className="lg:col-span-5">
              <div className="sticky top-32 space-y-6">
                <img
                  src={ASSETS.heritage}
                  alt="Original apothecary drawers at Bean in Stony"
                  className="w-full rounded-lg shadow-heritage-lg"
                />
                <div className="bg-walnut p-6 rounded-lg">
                  <p className="font-accent text-sage text-2xl mb-2">
                    "Life is too short for bad coffee"
                  </p>
                  <p className="text-cream/80 text-sm">
                    — Our philosophy, inscribed in our historic walls
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="section-padding bg-walnut" data-testid="features-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl md:text-5xl text-cream mb-4">
              What Makes Us Special
            </h2>
            <p className="text-cream/70 text-lg max-w-2xl mx-auto">
              A unique blend of history, hospitality, and homemade goodness
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: History,
                title: "Historic Setting",
                description: "Dine surrounded by original 1700s apothecary features, Tiffany lamps, and exposed brick walls."
              },
              {
                icon: Coffee,
                title: "Made In House",
                description: "Every cake, scone, and treat is baked fresh on-site daily using traditional recipes."
              },
              {
                icon: Armchair,
                title: "Indoor & Outdoor Seating",
                description: "Choose between our cosy historic interior or our beautiful vine-covered garden terrace."
              },
              {
                icon: Dog,
                title: "Dog Friendly",
                description: "Your four-legged friends are always welcome! We provide water bowls and treats for furry visitors."
              },
              {
                icon: Wifi,
                title: "Free WiFi",
                description: "Stay connected with complimentary high-speed WiFi throughout the café and garden."
              },
              {
                icon: Phone,
                title: "Call & Collect for Our Hot Sandwiches",
                description: "Hot baps and baguettes ready for your arrival. Call ahead to pre-order."
              }
            ].map((feature, index) => (
              <div 
                key={index}
                className="bg-cream/10 backdrop-blur-sm p-8 rounded-lg border border-cream/20 hover:border-sage/50 transition-all duration-300"
                data-testid={`feature-${index}`}
              >
                <feature.icon className="w-10 h-10 text-sage mb-4" />
                <h3 className="font-serif text-2xl text-cream mb-3">{feature.title}</h3>
                <p className="text-cream/70">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Garden Section */}
      <section className="section-padding" data-testid="garden-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-sage uppercase tracking-widest text-sm font-medium mb-4">
                Al Fresco Dining
              </p>
              <h2 className="font-serif text-4xl md:text-5xl text-walnut mb-6">
                Our Secret Garden
              </h2>
              <p className="text-walnut/80 text-lg leading-relaxed mb-6">
                Beyond our historic interior lies a hidden oasis — our lush, vine-covered 
                garden terrace. Draped in cascading greenery, this tranquil space offers 
                the perfect retreat from the bustle of the High Street.
              </p>
              <p className="text-walnut/80 text-lg leading-relaxed mb-8">
                Whether you're enjoying a leisurely breakfast, a light lunch, or afternoon 
                tea in the sunshine, our garden provides a magical setting that feels a 
                world away from everyday life.
              </p>
              <Link
                to="/contact"
                className="btn-primary inline-flex items-center gap-2"
                data-testid="garden-cta"
              >
                Get in Touch
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
            <div>
              <img
                src={ASSETS.outdoor}
                alt="Bean in Stony secret garden with vine-covered seating"
                className="w-full rounded-lg shadow-heritage-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="section-padding relative overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(rgba(75, 54, 33, 0.85), rgba(75, 54, 33, 0.85)), url(${ASSETS.brick})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-serif text-4xl md:text-5xl text-cream mb-6">
            Come Write Your Chapter
          </h2>
          <p className="text-cream/80 text-lg mb-10 max-w-2xl mx-auto">
            For over three centuries, this building has welcomed visitors. 
            We'd be honoured to welcome you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/menu"
              className="btn-primary bg-sage hover:bg-sage-dark"
              data-testid="about-cta-menu"
            >
              View Our Menu
            </Link>
            <Link
              to="/contact"
              className="btn-secondary border-cream text-cream hover:bg-cream hover:text-walnut"
              data-testid="about-cta-contact"
            >
              Get Directions
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
